import React, { useEffect, useState, useRef } from 'react';
import Container from '../../../Container/Container';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useForm, Controller } from "react-hook-form";
import Select from 'react-select';
import { get, } from '../../../ApiServices/ApiServices';

function AddBrideDetail({ onSubmit, onNext, onBack, initialValues }) {
    const { register, handleSubmit, formState: { errors }, control, getValues, watch, setValue, reset } = useForm({
        defaultValues: initialValues || {
            BrideName: '',
            bride_last_name: '',
            SameAddress: 'No',
            BrideAddress: '',
            BrideState: '',
            BrideCity: '',
            BrideZipCode: ''
        }
    });

    const sameAddress = watch("SameAddress");
    const location = useLocation();
    const [getState, setGetState] = useState([]);
    const originalBrideAddress = useRef(null);

    // Google Maps Autocomplete States
    const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
    const [brideAutocomplete, setBrideAutocomplete] = useState(null);
    const GOOGLE_MAPS_API_KEY = 'AIzaSyB5WrOGc_JpDhLbKfHO_CnzRircdez4GLA';

    // US States mapping
    const US_STATES_MAP = {
        'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
        'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
        'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
        'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
        'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
        'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
        'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
        'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
        'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
        'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
        'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
        'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
        'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia'
    };

    // Load Google Maps Script
    const loadGoogleMapsScript = () => {
        if (window.google && window.google.maps) {
            setIsGoogleMapsLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => setIsGoogleMapsLoaded(true);
        document.head.appendChild(script);
    };

    // Initialize Google Maps on mount
    useEffect(() => {
        loadGoogleMapsScript();
    }, []);

    // Initialize bride autocomplete
    useEffect(() => {
        if (isGoogleMapsLoaded && window.google && sameAddress === "No") {
            const timer = setTimeout(() => {
                initializeBrideAutocomplete();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isGoogleMapsLoaded, sameAddress]);

    // Initialize Bride Address Autocomplete
    const initializeBrideAutocomplete = () => {
        if (!window.google || !window.google.maps) return;

        const brideAddressInput = document.getElementById('bride-address-input');
        if (!brideAddressInput) return;

        // Clear existing autocomplete if any
        if (brideAutocomplete) {
            window.google.maps.event.clearInstanceListeners(brideAddressInput);
        }

        const newAutocomplete = new window.google.maps.places.Autocomplete(
            brideAddressInput,
            {
                types: ['address'],
                componentRestrictions: { country: 'us' }
            }
        );

        newAutocomplete.addListener('place_changed', () => handleBridePlaceSelect(newAutocomplete));
        setBrideAutocomplete(newAutocomplete);
    };

    // Handle Bride Address Place Selection
    const handleBridePlaceSelect = (autocompleteInstance) => {
        const place = autocompleteInstance.getPlace();

        if (!place.geometry) {
            console.error('No details available for this address');
            return;
        }

        let streetNumber = '';
        let route = '';
        let city = '';
        let stateCode = '';
        let stateName = '';
        let zipCode = '';

        place.address_components.forEach(component => {
            const types = component.types;

            if (types.includes('street_number')) {
                streetNumber = component.long_name;
            } else if (types.includes('route')) {
                route = component.long_name;
            } else if (types.includes('locality')) {
                city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
                stateCode = component.short_name;
                stateName = component.long_name;
            } else if (types.includes('postal_code')) {
                zipCode = component.long_name;
            }
        });

        const fullAddress = streetNumber && route ? `${streetNumber} ${route}` : place.name;
        const fullStateName = US_STATES_MAP[stateCode] || stateName;

        // Update bride form values and store in originalBrideAddress
        setValue('BrideAddress', fullAddress);
        setValue('BrideCity', city);
        setValue('BrideState', fullStateName);
        setValue('BrideZipCode', zipCode);

        // Update the ref so values persist when toggling "Same Address"
        originalBrideAddress.current = {
            BrideAddress: fullAddress,
            BrideState: fullStateName,
            BrideCity: city,
            BrideZipCode: zipCode
        };
    };

    // Store original bride address when component mounts
    useEffect(() => {
        if (initialValues) {
            originalBrideAddress.current = {
                BrideAddress: initialValues.BrideAddress || "",
                BrideState: initialValues.BrideState || "",
                BrideCity: initialValues.BrideCity || "",
                BrideZipCode: initialValues.BrideZipCode || ""
            };

            // Initialize SameAddress based on address comparison
            const addressesSame = (
                initialValues.Address === initialValues.BrideAddress &&
                initialValues.State === initialValues.BrideState &&
                initialValues.City === initialValues.BrideCity &&
                initialValues.ZipCode === initialValues.BrideZipCode
            );
            setValue("SameAddress", addressesSame ? "Yes" : "No");
        }
    }, [initialValues, setValue]);

    // Handle same address selection changes
    useEffect(() => {
        if (sameAddress === "Yes") {
            // Store current bride address before overwriting
            const currentValues = getValues();
            originalBrideAddress.current = {
                BrideAddress: currentValues.BrideAddress || "",
                BrideState: currentValues.BrideState || "",
                BrideCity: currentValues.BrideCity || "",
                BrideZipCode: currentValues.BrideZipCode || ""
            };

            // Set bride's address fields to groom's address values
            setValue("BrideAddress", initialValues?.Address || "");
            setValue("BrideState", initialValues?.State || "");
            setValue("BrideCity", initialValues?.City || "");
            setValue("BrideZipCode", initialValues?.ZipCode || "");
        } else if (sameAddress === "No" && originalBrideAddress.current) {
            // Restore the original bride address values
            setValue("BrideAddress", originalBrideAddress.current.BrideAddress);
            setValue("BrideState", originalBrideAddress.current.BrideState);
            setValue("BrideCity", originalBrideAddress.current.BrideCity);
            setValue("BrideZipCode", originalBrideAddress.current.BrideZipCode);
        }
    }, [sameAddress, initialValues, setValue, getValues]);

    const BrideAddress = watch("BrideAddress")
    const BrideState = watch("BrideState")
    const BrideCity = watch("BrideCity")
    const BrideZipCode = watch("BrideZipCode")

    console.log(BrideAddress, "BrideAddress")
    console.log(BrideState, "BrideState")
    console.log(BrideCity, "BrideCity")
    console.log(BrideZipCode, "BrideZipCode")

    const handleBack = () => {
        const currentValues = getValues();
        onBack(currentValues);
    };

    const ViewState = async () => {
        try {
            const api = `get-states`;
            const response = await get(api);
            if (response?.message == "success") {
                setGetState(response.data);
            }
        } catch (err) {
            console.error('Error:', err);
        }
    };

    useEffect(() => {
        ViewState();
    }, []);

    return (
        <Container>
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <div className="nk-block-head nk-block-head-sm">
                                <div className="nk-block-between">
                                    <div className="nk-block-head-content">
                                        <h3 className="nk-block-title page-title">{location.pathname == "/add-wedding-order" ? "Add" : "Update"} Weddings Event</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="card card-full">
                                <div className="card-inner">
                                    <div className="stepperLeft">
                                        <div className="row">
                                            <div className="col-md-4 brIght">
                                                <section className="section1">
                                                    <header>
                                                        <div className="bead complete"><span><i className="fa-solid fa-check"></i></span></div>
                                                        {location.pathname == "/add-wedding-order" ? "Add" : "Update"} Groom Detail
                                                    </header>
                                                </section>
                                                <section className="section2">
                                                    <header>
                                                        <div className="bead current"><span>2</span></div>
                                                        {location.pathname == "/add-wedding-order" ? "Add" : "Update"} Bride Detail
                                                    </header>
                                                </section>
                                                {location.pathname == "/add-wedding-order" && (
                                                    <section>
                                                        <header>
                                                            <div className="bead"><span>3</span></div>Groom Style Detail
                                                        </header>
                                                    </section>
                                                )}
                                                {location.pathname == "/add-wedding-order" && (
                                                    <section>
                                                        <header>
                                                            <div className="bead"><span>4</span></div> Groomsmen Style Detail
                                                        </header>
                                                    </section>
                                                )}
                                                <section>
                                                    <header>
                                                        <div className="bead"><span>{location.pathname == "/update-wedding-order" ? 3 : 5}</span></div>
                                                        {location.pathname == "/add-wedding-order" ? "Add" : "Update"} Description
                                                    </header>
                                                </section>
                                            </div>

                                            <div className="col-md-8">
                                                <h4>Bride Detail</h4>
                                                <form onSubmit={handleSubmit(onNext)}>
                                                    <div className="inputCardBox mt-4">
                                                        <div className="row">
                                                            <div className="col-md-6 mb-3">
                                                                <label>Bride's Name <span className="text-red-500">*</span></label>
                                                                <input
                                                                    className="form-control"
                                                                    type="text"
                                                                    placeholder="Enter name"
                                                                    {...register("BrideName", {
                                                                        required: "Bride's name is required",
                                                                        pattern: {
                                                                            value: /^[A-Za-z0-9 ]+$/,
                                                                            message: "Only letters and numbers are allowed"
                                                                        }
                                                                    })}
                                                                />
                                                                {errors.BrideName && <p className='text-red-500'>{errors.BrideName.message}</p>}
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label>Bride Last Name <span className="text-red-500">*</span></label>
                                                                <input
                                                                    className="form-control"
                                                                    type="text"
                                                                    placeholder="Enter last name"
                                                                    {...register("bride_last_name", {
                                                                        required: "Last name is required",
                                                                        pattern: {
                                                                            value: /^[A-Za-z0-9 ]+$/,
                                                                            message: "Only letters and numbers are allowed"
                                                                        }
                                                                    })}
                                                                />
                                                                {errors.bride_last_name && <p className='text-red-500'>{errors.bride_last_name.message}</p>}
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label>Same Address <span className="text-red-500">*</span></label>
                                                                <select
                                                                    className="form-control"
                                                                    {...register("SameAddress", {
                                                                        required: "This field is required",
                                                                    })}
                                                                >
                                                                    <option value="">Select</option>
                                                                    <option value="Yes">Yes</option>
                                                                    <option value="No">No</option>
                                                                </select>
                                                                {errors.SameAddress && <p className='text-red-500'>{errors.SameAddress.message}</p>}
                                                                <p><small>Is Bride's address the same as Groom's?</small></p>
                                                            </div>

                                                            <div className="col-md-6 mb-3">
                                                                <label>Address <span className="text-red-500"></span></label>
                                                                <input
                                                                    id="bride-address-input"
                                                                    className="form-control"
                                                                    type="text"
                                                                    placeholder={sameAddress === "Yes" ? "Using groom's address" : "Start typing address..."}
                                                                    {...register("BrideAddress", {
                                                                        required: sameAddress === "No" ? "Address is required when not same as groom" : false
                                                                    })}
                                                                    disabled={sameAddress === "Yes"}
                                                                />
                                                                {errors.BrideAddress && <p className='text-red-500'>{errors.BrideAddress.message}</p>}
                                                                {sameAddress === "No" && (
                                                                    <div className="text-xs text-gray-500 mt-1">
                                                                        Start typing to see address suggestions
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="col-md-6 mb-3 stateSelect">
                                                                <label>State <span className="text-red-500"></span></label>
                                                                <Controller
                                                                    name="BrideState"
                                                                    control={control}
                                                                    render={({ field, fieldState: { error } }) => (
                                                                        <div>
                                                                            <Select
                                                                                {...field}
                                                                                options={getState.map(item => ({
                                                                                    value: item.name,
                                                                                    label: item.name
                                                                                }))}
                                                                                placeholder="Select state"
                                                                                isClearable
                                                                                onChange={(selectedOption) => {
                                                                                    field.onChange(selectedOption ? selectedOption.value : '');
                                                                                }}
                                                                                value={getState.map(item => ({
                                                                                    value: item.name,
                                                                                    label: item.name
                                                                                })).find(option => option.value === field.value) || null}
                                                                                className={`react-select ${error ? 'is-invalid' : ''}`}
                                                                                classNamePrefix="react-select"
                                                                                isDisabled={sameAddress === "Yes"}
                                                                            />
                                                                            {error && <p className="text-red-500">{error.message}</p>}
                                                                        </div>
                                                                    )}
                                                                />
                                                            </div>

                                                            <div className="col-6 col-md-6 mb-3">
                                                                <label>City <span className="text-red-500"></span></label>
                                                                <input
                                                                    className="form-control"
                                                                    type="text"
                                                                    placeholder="Enter city"
                                                                    {...register("BrideCity", {
                                                                        required: sameAddress === "No" ? "City is required when not same as groom" : false
                                                                    })}
                                                                    disabled={sameAddress === "Yes"}
                                                                />
                                                                {errors.BrideCity && <p className='text-red-500'>{errors.BrideCity.message}</p>}
                                                            </div>

                                                            <div className="col-6 col-md-6 mb-3">
                                                                <label>Zip Code <span className="text-red-500"></span></label>
                                                                <input
                                                                    className="form-control"
                                                                    type="text"
                                                                    placeholder="Enter zipcode"
                                                                    {...register("BrideZipCode", {
                                                                        required: sameAddress === "No" ? "Zip code is required when not same as groom" : false,
                                                                        pattern: {
                                                                            value: /^\d{5}(?:[-\s]\d{4})?$/,
                                                                            message: "Invalid zip code format"
                                                                        }
                                                                    })}
                                                                    disabled={sameAddress === "Yes"}
                                                                />
                                                                {errors.BrideZipCode && <p className='text-red-500'>{errors.BrideZipCode.message}</p>}
                                                            </div>

                                                            <div className="col-md-12 mt-3">
                                                                <a className="backBtnWedding" style={{ cursor: "pointer" }} onClick={handleBack}>Back</a>
                                                                <button type="submit" className="addBtnCustomer">Continue</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    );
}

export default AddBrideDetail;